import { useEffect, useState } from "react";
import { fetchLastPayments, searchPayments } from "../paymentsApi";

const SEARCH_DEBOUNCE_MS = 300;

/**
 * Pushes a /last request through the setters, swallowing AbortController noise.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function runLast({ signal, setItems, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await fetchLastPayments();
        if (!signal.aborted) setItems(data);
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible de charger les paiements");
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Pushes a /search/<term> request through the setters.
 * @param {Object} args - Fetch arguments
 * @returns {Promise<void>}
 */
async function runSearch({ term, signal, setItems, setError, setLoading }) {
    setLoading(true);
    setError(null);
    try {
        const data = await searchPayments(term);
        if (!signal.aborted) setItems(data);
    } catch (err) {
        if (!signal.aborted) {
            const msg = err?.response?.data;
            setError(typeof msg === "string" ? msg : "Impossible d'effectuer la recherche");
        }
    } finally {
        if (!signal.aborted) setLoading(false);
    }
}

/**
 * Hook driving the "Paiements" tab: returns /last when the query is empty,
 * /search/<term> debounced 300 ms otherwise. Aborts in-flight calls on cleanup.
 * @param {string} query - Current search query
 * @returns {{ items: Array, loading: boolean, error: string|null, active: boolean }}
 */
function usePaymentsBrowse(query) {
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const trimmed = (query || "").trim();
    const active = trimmed.length > 0;

    useEffect(() => {
        const controller = new AbortController();
        const handle = setTimeout(() => {
            const args = { signal: controller.signal, setItems, setError, setLoading };
            if (active) runSearch({ term: trimmed, ...args });
            else runLast(args);
        }, active ? SEARCH_DEBOUNCE_MS : 0);
        return () => { clearTimeout(handle); controller.abort(); };
    }, [trimmed, active]);

    return { items, loading, error, active };
}

export default usePaymentsBrowse;
